import React, { Fragment, useContext, useEffect, useState } from "react";
import { Button, CircularProgress, IconButton } from "@mui/material";
import { api, IPrinterLabelList, PrinterLabel, PrinterLabelType, RequestStatus } from "../../../logic/api";
import flex from "../../../styles/flex";
import { SessionContext } from "../../../utility/useSession";
import { FiPlus, FiPrinter, FiSave } from "react-icons/fi";
import TextInputField from "../../editable/EditableField/fields/TextInputField";
import ErrorDialog from "../../notifications/errorDialog/ErrorDialog";
import { useLabelPrinter, ZebraLabelPrinter } from "..";
import TabbedView from "../../lists/TabbedView/TabbedView";
import TabbedViewPage from "../../lists/TabbedView/TabbedViewPage";
import PrinterLabelPreview from "./printerLabelPreview";
import DropDownField from "../../editable/EditableField/fields/DropDownField";
import MoneyInputField from "../../editable/EditableField/fields/MoneyInputField";
import { retrieveValue } from "../../../utility/storage";
import NumberInputField from "../../editable/EditableField/fields/NumberInputField";
import { formatCamel } from "../../../utility/utils";
import { useTranslation } from "react-i18next";
import { usePreferences } from "../../../utility/usePreferences";

const PrinterLabelEditor = () => {
    const session = useContext(SessionContext);
    const [params, setParams] = useState<IPrinterLabelList>({});
    const listLabels = api.hooks.printerLabel.list();
    const saveLabel = api.hooks.printerLabel.update();
    const { getZPLParameters, mergeZPLParameters } = useLabelPrinter();
    const { t } = useTranslation();

    const [selectedLabelType, setSelectedLabelType] = useState<number | null>(null);
    const { enable_device_zebra_labels } = usePreferences();
    const isMobileMarkUpLabel = enable_device_zebra_labels == false && selectedLabelType != null && [PrinterLabelType.mobileKeyTag.toString(), PrinterLabelType.mobileCollectionReceipt.toString()].indexOf(selectedLabelType.toString()) != -1;

    const [labels, setLabels] = useState<PrinterLabel[]>([]);
    const [printErr, setPrintErr] = useState<Error | null>(null);
    const [saveComplete, setSaveComplete] = useState<Error | null>(null);

    const carParkId = (session.current.site == null || session.current.site.home == null) ? null : session.current.site.home.id;
    const [currentCarParkId, setCurrentCarParkId] = useState(carParkId);

    useEffect(() => {
        if (carParkId != currentCarParkId) {
            setCurrentCarParkId(carParkId);
            setParams({ ...params });
        }
    }, [carParkId]);


    useEffect(() => {
        listLabels.send(session.current.context, params);
    }, [params]);

    useEffect(() => {
        if (listLabels.data != null) {
            setLabels(listLabels.data);
        }
    }, [listLabels.data]);

    const selectedLabel = labels.find((l) => {
        return l.type == selectedLabelType;
    });

    const save = async () => {
        if (selectedLabel == null) {
            return;
        }
        saveLabel.send(session.current.context, {
            type: selectedLabel.type,
            zpl: selectedLabel.zpl,
            params: selectedLabel.params
        }, (result) => {
            if (result != null) {
                setSaveComplete(new Error(t("labels.labelTemplateSaved")));
            }
        });
    }

    const [printing, setPrinting] = useState(false);


    let parameters: string[] = [];

    if (selectedLabel != null) {
        if (isMobileMarkUpLabel) {
            const getMarkUpParameters = (zpl: string) => {
                const parameters: string[] = [];

                //parameters defined as {paramName}
                const segments = (zpl || "").split("{");
                for (const s of segments) {
                    if (s.indexOf("}") != -1) {
                        const paramName = s.split("}")[0];
                        parameters.push(paramName);
                    }
                }


                return parameters;
            }
            parameters = getMarkUpParameters(selectedLabel.zpl);
        } else {
            parameters = getZPLParameters(selectedLabel.zpl);
        }

    }

    let testParams: Record<string, unknown> | null = null;
    if (selectedLabel != null) {
        try {
            testParams = JSON.parse(selectedLabel.params);
        } catch (ex) {
        }
    }
    const updateParam = (field: string, value?: string) => {
        const newParams = { ...(testParams || {}) }
        if (selectedLabel != null) {
            const lblIndex = labels.indexOf(selectedLabel);
            if (lblIndex != -1) {
                const lbls = [...labels];

                newParams[field] = value || "";
                lbls[lblIndex] = { ...selectedLabel, params: JSON.stringify(newParams) } as PrinterLabel
                setLabels(lbls);
            }
        }
    }
    const getParam = (field: string, defaultValue?: string) => {
        if (defaultValue == null) {
            defaultValue = "";
        }
        if (testParams == null) {
            return defaultValue;
        }
        const val = testParams[field] == null ? defaultValue : testParams[field] as string;
        if (val == null || val.length == 0) {
            return defaultValue;
        }
        return val;
    }

    const getMergedZPL = () => {
        if (selectedLabel == null) {
            return "";
        }
        return mergeZPLParameters(selectedLabel.zpl || "", testParams || {});
    }

    const testPrint = async () => {
        if (selectedLabel == null) {
            return;
        }
        setPrinting(true);
        try {
            const defaultPrinter = retrieveValue("defaultLabelPrinterUID", "");
            await ZebraLabelPrinter.sendToPrinter(defaultPrinter, getMergedZPL());
        } catch (ex: unknown) {
            let errorMessage = t("labels.errorOccured");

            if (typeof ex === 'string') {
                errorMessage = ex;
            }

            setPrintErr(new Error(errorMessage));
        }
        setPrinting(false);
    }

    const generatedZpl = getMergedZPL();


    return (
        <div style={{ marginTop: 20, marginLeft: 20 }}>
            <ErrorDialog err={saveComplete}></ErrorDialog>
            <ErrorDialog err={saveLabel.err}></ErrorDialog>
            <ErrorDialog err={printErr}></ErrorDialog>
            <div style={flex.flexRows}>
                <div style={{ ...flex.flexAuto, width: 150 }}>
                    <div style={{ paddingTop: 10, marginBottom: 45 }}>
                        <div style={{
                            backgroundColor: "#FBFBFB",
                            borderLeft: "solid 1px lightgray",
                            borderTop: "solid 1px lightgray",
                            borderBottom: "solid 1px lightgray",
                            borderTopLeftRadius: 5,
                            borderBottomLeftRadius: 5,
                        }}>
                            <div style={flex.flexRows}>
                                <div style={flex.flexAuto}>
                                    <div style={{ visibility: "hidden" }}>
                                        <IconButton disabled={true}>
                                            <FiPlus />
                                        </IconButton>
                                    </div>
                                </div>
                                <div style={{ ...flex.flexFill, textAlign: "center" }}>
                                    <div style={{ padding: 15, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                                        <b>{t("settings.labels")}</b>
                                    </div>
                                </div>
                                <div style={flex.flexAuto}>
                                    <div style={{ visibility: "hidden" }}>
                                        <IconButton disabled={true}>
                                            <FiPlus />
                                        </IconButton>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {
                        labels.map((label, key) => {

                            const isSelected = selectedLabelType == label.type;

                            return <Button key={key}

                                variant={isSelected == true ? "contained" : "outlined"}
                                color={isSelected == true ? "secondary" : "primary"}
                                style={{
                                    marginTop: 10,
                                    width: "100%",
                                }} onClick={() => {
                                    setSelectedLabelType(label.type);
                                }}>{formatCamel(label.name)}</Button>
                        })
                    }
                </div>
                <div style={flex.flexFill}>
                    <div style={{ paddingTop: 10 }}>
                        <div style={{
                            backgroundColor: "#FBFBFB",
                            borderTop: "solid 1px lightgray",
                            borderBottom: "solid 1px lightgray",
                        }}>
                            <div style={flex.flexRows}>
                                <div style={flex.flexAuto}>
                                    <div style={{ visibility: "hidden" }}>
                                        <IconButton disabled={true}>
                                            <FiPlus />
                                        </IconButton>
                                    </div>
                                </div>
                                <div style={{ ...flex.flexFill, textAlign: "center" }}>
                                    <div style={{ padding: 15, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                                        <b>{selectedLabel != null ? formatCamel(selectedLabel.name) : "Label Designer"}</b>
                                    </div>
                                </div>

                                <div style={flex.flexAuto}>
                                    <IconButton
                                        disabled={selectedLabel == null || saveLabel.status == RequestStatus.loading}
                                        onClick={() => {
                                            save();
                                        }}
                                    >
                                        {saveLabel.status == RequestStatus.loading && (
                                            <CircularProgress size={20}></CircularProgress>
                                        )}
                                        {saveLabel.status != RequestStatus.loading && (
                                            <FiSave />
                                        )}

                                    </IconButton>
                                </div>


                            </div>
                        </div>
                        <div style={{ padding: 10 }}>
                            <TabbedView animated={false}>
                                <TabbedViewPage visible={!isMobileMarkUpLabel} title={t("labels.preview")} >

                                    <div style={{ border: "solid 1px lightgray", borderRadius: 5, minHeight: 400, overflow: "hidden" }}>
                                        {selectedLabel != null && (
                                            <div style={{ padding: 10, marginTop: 10 }}>
                                                <div style={flex.flexRows}>
                                                    <div style={flex.flexFill}></div>
                                                    <div style={flex.flexAuto}>
                                                        <div style={{ marginRight: 10 }}>
                                                            <DropDownField label="dpmm"
                                                                selectedItem={{ name: "", value: getParam("_dpmm", "6dpmm") }}
                                                                onRequestData={async () => {
                                                                    return [
                                                                        { name: "6dpmm", value: "6dpmm" },
                                                                        { name: "8dpmm", value: "8dpmm" },
                                                                        { name: "12dpmm", value: "12dpmm" },
                                                                        { name: "24dpmm", value: "24dpmm" }
                                                                    ]
                                                                }}
                                                                onChange={(item) => {
                                                                    updateParam("_dpmm", item.value?.toString());
                                                                }}></DropDownField>
                                                        </div>
                                                    </div>
                                                    <div style={flex.flexAuto}>
                                                        <div style={{ marginRight: 10 }}>
                                                            <MoneyInputField
                                                                maxWidth={120}
                                                                label={t("labels.widthInch")}
                                                                value={parseFloat(getParam("_width", "4"))}
                                                                onChange={(val) => {
                                                                    if (val <= 0) {
                                                                        val = 0.01
                                                                    }
                                                                    if (val > 15) {
                                                                        val = 15
                                                                    }
                                                                    updateParam("_width", val.toString());
                                                                }}
                                                            ></MoneyInputField>
                                                        </div>
                                                    </div>
                                                    <div style={flex.flexAuto}>
                                                        <div style={{ marginRight: 10 }}>
                                                            <MoneyInputField
                                                                maxWidth={120}
                                                                label={t("labels.heightInch")}
                                                                value={parseFloat(getParam("_height", "2"))}
                                                                onChange={(val) => {
                                                                    if (val <= 0) {
                                                                        val = 0.01
                                                                    }
                                                                    if (val > 15) {
                                                                        val = 15
                                                                    }
                                                                    updateParam("_height", val.toString());
                                                                }}
                                                            ></MoneyInputField>
                                                        </div>
                                                    </div>
                                                    {

                                                        <div style={flex.flexAuto}>
                                                            <div style={{ marginRight: 10 }}>
                                                                <NumberInputField
                                                                    maxWidth={70}
                                                                    label={t("labels.index")}
                                                                    value={parseInt(getParam("_index", "0"))}
                                                                    onChange={(val) => {
                                                                        if (val < 0) {
                                                                            val = 0;
                                                                        }
                                                                        updateParam("_index", val.toString());
                                                                    }}
                                                                ></NumberInputField>
                                                            </div>
                                                        </div>

                                                    }
                                                    <div style={flex.flexFill}></div>
                                                </div>
                                            </div>
                                        )}

                                        <PrinterLabelPreview
                                            zpl={generatedZpl}
                                            dpmm={getParam("_dpmm", "6dpmm")}
                                            width={getParam("_width", "4")}
                                            height={getParam("_height", "6")}
                                            index={getParam("_index", "0")}

                                        ></PrinterLabelPreview>

                                    </div>
                                </TabbedViewPage>
                                <TabbedViewPage title={isMobileMarkUpLabel ? t("labels.markup") : t("labels.zplCode")} >
                                    <TextInputField
                                        label=""
                                        height={600}
                                        maxWidth={"auto"}
                                        multiLine={true}
                                        value={selectedLabel == null ? t("labels.noLabelSelected") : selectedLabel.zpl}
                                        onChange={(value) => {
                                            if (selectedLabel != null) {
                                                const lblIndex = labels.indexOf(selectedLabel);
                                                if (lblIndex != -1) {
                                                    const lbls = [...labels];
                                                    lbls[lblIndex] = { ...selectedLabel, zpl: value } as PrinterLabel
                                                    setLabels(lbls);
                                                }
                                            }
                                        }}>

                                    </TextInputField>
                                </TabbedViewPage>

                            </TabbedView>

                        </div>
                    </div>

                </div>
                <div style={{ ...flex.flexAuto, width: 200 }}>
                    <div style={{ paddingTop: 10 }}>
                        <div style={{
                            backgroundColor: "#FBFBFB",
                            borderRight: "solid 1px lightgray",
                            borderTop: "solid 1px lightgray",
                            borderBottom: "solid 1px lightgray",
                            borderTopRightRadius: 5,
                            borderBottomRightRadius: 5,
                        }}>
                            <div style={flex.flexRows}>
                                <div style={flex.flexAuto}>
                                    <div style={{ visibility: "hidden" }}>
                                        <IconButton disabled={true}>
                                            <FiPlus />
                                        </IconButton>
                                    </div>
                                </div>
                                <div style={{ ...flex.flexFill, textAlign: "center" }}>

                                    <div style={{ padding: 15, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                                        <b>{t("labels.parameters")}</b>
                                    </div>

                                </div>
                                {isMobileMarkUpLabel == false && (
                                    <div style={flex.flexAuto}>
                                        <IconButton
                                            disabled={selectedLabel == null || printing == true}
                                            onClick={() => {
                                                testPrint();
                                            }}
                                        >

                                            {printing == true && (
                                                <CircularProgress size={20}></CircularProgress>
                                            )}
                                            {printing == false && (
                                                <FiPrinter />
                                            )}
                                        </IconButton>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div style={{ paddingRight: 5, paddingTop: 55 }}>
                        {parameters.map((name, key) => {
                            return <div style={{ marginBottom: 10 }} key={key}>
                                {!isMobileMarkUpLabel && (
                                    <Fragment>
                                        <div style={{ textAlign: "right" }}><small>{name}</small></div>
                                        <TextInputField label={formatCamel(name)} value={getParam(name)} onChange={(val) => {
                                            updateParam(name, val);
                                        }}></TextInputField>
                                    </Fragment>
                                )}
                                {isMobileMarkUpLabel && (
                                    <Fragment>
                                        <div style={{ textAlign: "right" }}><b>{formatCamel(name)}</b></div>
                                        <div style={{ textAlign: "right" }}><small>{name}</small></div>

                                    </Fragment>
                                )}

                            </div>
                        })}
                    </div>
                </div>
            </div>

        </div>
    );
}

export default PrinterLabelEditor;